import React from 'react';
import PropTypes from 'prop-types';

import { TicketBox, Spacing, Text, Badge } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

/**
 * TicketList Functional Component
 *
 * Renders a list of tickets for the provided passengers and trip.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.passengers - The list of passengers associated with the tickets.
 * @param {Function} props.onClickedTicket - The function to handle when a ticket is clicked.
 * @param {boolean} props.cancelAllowed - Indicates if cancellation is allowed for this trip.
 *                                            It typically receives a ticket identifier or details as an argument.
 * @param {string} props.selectedTicketKey - The key of the currently selected ticket.
 *
 * @returns {JSX.Element} The rendered ticket list component.
 */
const TicketList = ({ passengers, onClickedTicket, selectedTicketKey, cancelAllowed }) => {
  const { t } = useTranslation('exchange');
  const { t: tSeats } = useTranslation('seats');
  const features = useWhitelabelFeatures();
  const isCompleteCancellation = features.ALLOW_COMPLETE_CANCELLATION;
  const showEnhancedTicketData = features.SHOW_ENHANCED_TICKET_DATA;

  /**
   * handleRadioChange Function
   *
   * Handles the change event for a radio input.
   *
   * This function is called when a radio button's selected value changes, and it processes the event accordingly.
   *
   * @param {Object} e - The event object generated by the radio button change.
   * @param {HTMLInputElement} e.target - The radio input element that triggered the change event.
   * @returns {void}
   */
  const handleRadioChange = (e) => {
    onClickedTicket(e.target.value);
  };

  /**
   * Renders the ticket details based on feature flags and data availability.
   *
   * @param {string} transporterKey - The ticket's transporter key.
   * @param {string} seatNumber - The seat number.
   * @param {string} level - The ticket level.
   * @returns {JSX.Element[]} Array of ticket detail elements to render.
   */
  const renderTicketDetails = (
    category,
    transporterKey,
    seatNumber,
    seatLevel,
    serviceTypeProvider,
  ) => {
    const items = [
      {
        key: 'category',
        label: t('ticket_list.category'),
        value: tSeats(`passengers:type.${category}`),
        condition: !showEnhancedTicketData && category,
      },
      {
        key: 'ticket-number',
        label: t('ticket_list.ticket_number'),
        value: transporterKey,
        condition: showEnhancedTicketData && transporterKey,
      },
      {
        key: 'seat',
        label: t('ticket_list.seat'),
        value: seatNumber,
        condition: !!seatNumber,
      },
      {
        key: 'seat-level',
        label: t('ticket_list.level'),
        value: seatLevel,
        condition: showEnhancedTicketData && seatLevel,
      },
      {
        key: 'service-type-provider',
        label: t('ticket_list.service_type'),
        value: serviceTypeProvider,
        condition: showEnhancedTicketData && serviceTypeProvider,
      },
    ];

    return (
      <div className="inline-grid grid-cols-2 gap-y-2 gap-x-6 w-fit">
        {items
          .filter(({ condition }) => condition)
          .map(({ key, label, value }) => (
            <Text key={key} size="S">
              {label + (label && ':')} {value}
            </Text>
          ))}
      </div>
    );
  };

  /**
   * Renders the status or selection input for a ticket.
   *
   * @param {object} passenger - The passenger data.
   * @returns {JSX.Element | null} The status text or radio input.
   */
  const renderTicketStatusOrInput = (passenger) => {
    const { state, statusProvider, transporterKey } = passenger;
    const isCanceled = state === 'canceled' || state === 'refund_requested';
    const hasCustomStatus = statusProvider && statusProvider.trim() !== '';

    if (isCanceled) {
      return (
        <span style={{ color: '#cf0101' }}>
          {hasCustomStatus ? statusProvider : t('ticket_list.state_cancelled')}
        </span>
      );
    }

    if (!cancelAllowed) {
      return <span style={{ color: '#cf0101' }}>{t('ticket_list.cant_cancel')}</span>;
    }

    if (!isCompleteCancellation) {
      return (
        <input
          type="radio"
          name="ticket"
          id={transporterKey}
          value={transporterKey}
          onChange={handleRadioChange}
          checked={selectedTicketKey === transporterKey}
        />
      );
    }

    return null;
  };

  return passengers.map((passenger) => {
    const {
      category,
      transporterKey,
      seatNumber,
      seatLevel,
      firstName,
      lastName,
      secondLastName,
      serviceTypeProvider,
      statusProvider,
    } = passenger;

    const hasStatusBadge = statusProvider && statusProvider.trim() !== '';

    return (
      <div id={transporterKey} key={transporterKey}>
        <TicketBox className="relative">
          <Spacing size="XS" vertical fullWidth>
            <Spacing alignItems="center" justifyContent="space-between" fullWidth>
              <Spacing alignItems="center" size="S">
                <Text weight="semibold">
                  {firstName} {lastName} {secondLastName}
                </Text>
                {hasStatusBadge && (
                  <Badge type="success" size="small" roundCorners smallPadding>
                    <Text size="S" weight="semibold" color="white">
                      {statusProvider}
                    </Text>
                  </Badge>
                )}
              </Spacing>
              {renderTicketStatusOrInput(passenger)}
            </Spacing>
            <Spacing isResponsive responsiveSize="XS">
              {renderTicketDetails(
                category,
                transporterKey,
                seatNumber,
                seatLevel,
                serviceTypeProvider,
              )}
            </Spacing>
          </Spacing>
        </TicketBox>
      </div>
    );
  });
};

TicketList.propTypes = {
  passengers: PropTypes.array,
  onClickedTicket: PropTypes.func,
  selectedTicketKey: PropTypes.string,
  cancelAllowed: PropTypes.bool,
};

export default TicketList;
